/**
 * F9 Endpoints — Centralized, typed endpoint definitions for Dashboard,
 * Goals, Sharing, Notifications, and User Statistics.
 * Extends the existing endpoints.ts with the full F9 backend API surface.
 *
 * The /api/v1 prefix is already in the axios instance baseURL
 * (VITE_API_BASE_URL in client.ts), so ep() only ensures paths start with '/'.
 */

import type {
  PaginationParams,
  LessonProgress,
  UpdateLessonProgressPayload,
  LegalTrackingDeckReport,
  LegalTrackingCollectionReport,
  BlockProgressPayload,
  BlockProgressResponse,
} from './types';
import type {
  DashboardOverview,
  CriticalPoint,
  StreakInfo,
  RetentionInfo,
  HeatmapCell,
  GoalProgressDetail,
  GoalFilters,
  PublicDeck,
  MyShare,
  SharedWithMeDeck,
  SharedDeckPreview,
  CopyDeckPayload,
  CopyDeckResult,
  UserStatistics,
} from './types-f9';
import type {
  Goal,
  CreateGoalPayload,
  UpdateGoalPayload,
  AppNotification,
  PaginatedResponse,
  User,
  UpdateUserPayload,
} from './types';

/**
 * Build a relative endpoint path.
 * The /api/v1 prefix lives in the axios baseURL, so we only ensure
 * the path starts with '/'.  This prevents the double-prefix bug
 * /api/v1/api/v1/... that occurs when ep() also prepends /api/v1.
 */
function ep(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

function buildQuery(params?: PaginationParams): string {
  if (!params) return '';
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortOrder) query.set('sortOrder', params.sortOrder);
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

// ============================================================
// Dashboard Endpoints (F9)
// ============================================================

export const dashboardF9Endpoints = {
  overview: ep('/dashboard/overview'),
  heatmap: (days?: number) => ep(`/dashboard/heatmap?days=${days ?? 365}`),
  accuracy: ep('/dashboard/accuracy'),
  progression: (days?: number) => ep(`/dashboard/progression${days ? `?days=${days}` : ''}`),
  criticalPoints: ep('/dashboard/critical-points'),
  goalsProgress: ep('/dashboard/goals-progress'),
  streak: ep('/dashboard/streak'),
  retention: ep('/dashboard/retention'),
} as const;

// ============================================================
// Goal Endpoints (F9)
// ============================================================

export const goalF9Endpoints = {
  list: (params?: PaginationParams & GoalFilters) => {
    let qs = '';
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.status) sp.set('status', params.status);
    if (params?.type) sp.set('type', params.type);
    qs = sp.toString();
    return ep(`/goals${qs ? `?${qs}` : ''}`);
  },
  getById: (id: string) => ep(`/goals/${id}`),
  create: ep('/goals'),
  update: (id: string) => ep(`/goals/${id}`),
  delete: (id: string) => ep(`/goals/${id}`),
  pause: (id: string) => ep(`/goals/${id}/pause`),
  resume: (id: string) => ep(`/goals/${id}/resume`),
  overdue: ep('/goals/overdue'),
  progress: (id: string) => ep(`/goals/${id}/progress`),
} as const;

// ============================================================
// Sharing Endpoints (F9)
// ============================================================

export const sharingF9Endpoints = {
  browseDecks: (params?: PaginationParams & { search?: string; category?: string; tag?: string }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.search) sp.set('search', params.search);
    if (params?.category) sp.set('category', params.category);
    if (params?.tag) sp.set('tag', params.tag);
    const qs = sp.toString();
    return ep(`/sharing/decks${qs ? `?${qs}` : ''}`);
  },
  getDeckByToken: (token: string) => ep(`/sharing/decks/${token}`),
  copyDeck: (deckId: string) => ep(`/sharing/decks/${deckId}/copy`),
  myShares: (params?: PaginationParams) => ep(`/sharing/my-shares${buildQuery(params)}`),
  sharedWithMe: ep('/sharing/shared-with-me'),
  revokeShare: (id: string) => ep(`/sharing/${id}`),
} as const;

// ============================================================
// Notification Endpoints (F9)
// ============================================================

export const notificationF9Endpoints = {
  list: (params?: PaginationParams) => ep(`/notifications${buildQuery(params)}`),
  markAsRead: (id: string) => ep(`/notifications/${id}/read`),
  markAllAsRead: ep('/notifications/read-all'),
  delete: (id: string) => ep(`/notifications/${id}`),
} as const;

// ============================================================
// User Endpoints
// ============================================================

export const userF9Endpoints = {
  me: ep('/users/me'),
  updateMe: ep('/users/me'),
  statistics: ep('/users/me/statistics'),
} as const;

// ============================================================
// Type helpers for F9 endpoint responses
// ============================================================

export type DashboardF9Api = {
  overview: { response: DashboardOverview };
  heatmap: { params?: { days: number }; response: HeatmapCell[] };
  accuracy: { response: { category: string; accuracy: number; reviewCount: number; cardCount: number }[] };
  progression: { params?: { days: number }; response: { date: string; cardsReviewed: number; timeSpentMinutes: number }[] };
  criticalPoints: { response: CriticalPoint[] };
  goalsProgress: { response: GoalProgressDetail[] };
  streak: { response: StreakInfo };
  retention: { response: RetentionInfo };
};

export type GoalF9Api = {
  list: { params?: PaginationParams & GoalFilters; response: PaginatedResponse<Goal> };
  getById: { params: { id: string }; response: Goal };
  create: { body: CreateGoalPayload; response: Goal };
  update: { params: { id: string }; body: UpdateGoalPayload; response: Goal };
  delete: { params: { id: string }; response: void };
  pause: { params: { id: string }; response: Goal };
  resume: { params: { id: string }; response: Goal };
  overdue: { response: Goal[] };
  progress: { params: { id: string }; response: GoalProgressDetail };
};

export type SharingF9Api = {
  browseDecks: { params?: PaginationParams & { search?: string; category?: string; tag?: string }; response: PaginatedResponse<PublicDeck> };
  getDeckByToken: { params: { token: string }; response: SharedDeckPreview };
  copyDeck: { params: { deckId: string }; body: CopyDeckPayload; response: CopyDeckResult };
  myShares: { response: MyShare[] };
  sharedWithMe: { response: SharedWithMeDeck[] };
  revokeShare: { params: { id: string }; response: void };
};

export type NotificationF9Api = {
  list: { params?: PaginationParams; response: PaginatedResponse<AppNotification> };
  markAsRead: { params: { id: string }; response: void };
  markAllAsRead: { response: void };
  delete: { params: { id: string }; response: void };
};

export type UserF9Api = {
  me: { response: User };
  updateMe: { body: UpdateUserPayload; response: User };
  statistics: { response: UserStatistics };
};

// ============================================================
// Lesson Progress & Legal Tracking Endpoints
// ============================================================

export const lessonProgressEndpoints = {
  start: (lessonId: string) => ep(`/lessons/${lessonId}/start`),
  complete: (lessonId: string) => ep(`/lessons/${lessonId}/complete`),
  update: (lessonId: string) => ep(`/lessons/${lessonId}/progress`),
  blockProgress: (lessonId: string) => ep(`/lessons/${lessonId}/block-progress`),
} as const;

export const legalTrackingEndpoints = {
  deck: (deckId: string) => ep(`/decks/${deckId}/legal-tracking`),
  collection: (deckListId: string) => ep(`/deck-lists/${deckListId}/legal-tracking`),
  exportDeck: (deckId: string, format: 'pdf' | 'csv') => ep(`/decks/${deckId}/legal-tracking/export?format=${format}`),
  exportCollection: (deckListId: string, format: 'pdf' | 'csv') => ep(`/deck-lists/${deckListId}/legal-tracking/export?format=${format}`),
} as const;

export type LessonProgressApi = {
  start: { params: { lessonId: string }; response: LessonProgress };
  complete: { params: { lessonId: string }; response: LessonProgress };
  update: { params: { lessonId: string }; body: UpdateLessonProgressPayload; response: LessonProgress };
  blockProgress: { params: { lessonId: string }; body: BlockProgressPayload; response: BlockProgressResponse };
};

export type LegalTrackingApi = {
  deck: { params: { deckId: string }; response: LegalTrackingDeckReport };
  collection: { params: { deckListId: string }; response: LegalTrackingCollectionReport };
};